import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import { useTranslation } from '@frontend/i18n';
import { Button, SpinningLoader, Text } from '@frontend/design-system';
import { pageCanvasStyle, pageErrorStyle, pageLoaderStyle } from './pdf-field.styles';
import type { PdfPageSize } from './pdf-placement';

/** Upper bound on backing-store pixels per page; large pages on high-DPR screens otherwise exhaust canvas memory. */
const MAX_CANVAS_PIXELS = 16_000_000;
/** Re-renders after a size change (zoom) wait this long; meanwhile the previous bitmap is stretched to fit. */
const RERENDER_DELAY_MS = 150;

interface PdfPageCanvasProps {
  doc: PDFDocumentProxy;
  pageIndex: number;
  pageSize: PdfPageSize;
  /** CSS width the page is displayed at. */
  width: number;
  /** Scroll container used for lazy rendering. Defaults to the viewport. */
  scrollRoot?: Element | null;
  ariaLabel: string;
  onRenderError?: (error: unknown) => void;
}

/**
 * Renders a single page only while it's near the viewport and frees the canvas backing store once it scrolls
 * away, so memory stays flat regardless of page count.
 */
export const PdfPageCanvas = ({
  doc,
  pageIndex,
  pageSize,
  width,
  scrollRoot,
  ariaLabel,
  onRenderError,
}: PdfPageCanvasProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [renderError, setRenderError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isRenderedRef = useRef(false);
  isRenderedRef.current = isRendered;
  const onRenderErrorRef = useRef(onRenderError);
  onRenderErrorRef.current = onRenderError;
  const height = pageSize.width > 0 ? (width * pageSize.height) / pageSize.width : width;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
      root: scrollRoot ?? null,
      rootMargin: '200% 0px',
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [scrollRoot]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isVisible || width <= 0) return;

    let cancelled = false;
    let page: PDFPageProxy | undefined;
    let task: RenderTask | undefined;

    const render = async () => {
      page = await doc.getPage(pageIndex + 1);
      if (cancelled) return;
      const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      let scale = (width / page.getViewport({ scale: 1 }).width) * dpr;
      const base = page.getViewport({ scale });
      const pixels = base.width * base.height;
      if (pixels > MAX_CANVAS_PIXELS) scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
      const viewport = page.getViewport({ scale });

      // Draw off-screen so the previous render stays visible until the new one is ready.
      const buffer = document.createElement('canvas');
      buffer.width = Math.floor(viewport.width);
      buffer.height = Math.floor(viewport.height);
      task = page.render({ canvas: buffer, viewport });
      await task.promise;
      if (cancelled) return;
      canvas.width = buffer.width;
      canvas.height = buffer.height;
      canvas.getContext('2d')?.drawImage(buffer, 0, 0);
      buffer.width = 0;
      buffer.height = 0;
      setRenderError(false);
      setIsRendered(true);
    };

    // First paint is immediate; later size changes are debounced so zooming doesn't re-render every step.
    const timeoutId = setTimeout(
      () =>
        render().catch((error: unknown) => {
          if (cancelled || (error as { name?: string })?.name === 'RenderingCancelledException') return;
          setRenderError(true);
          onRenderErrorRef.current?.(error);
        }),
      isRenderedRef.current ? RERENDER_DELAY_MS : 0
    );

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      task?.cancel();
    };
  }, [doc, pageIndex, width, isVisible, retryCount]);

  // Release memory once the page leaves the render window.
  useEffect(() => {
    if (isVisible) return;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
    setIsRendered(false);
    doc
      .getPage(pageIndex + 1)
      .then((page) => page.cleanup())
      .catch(() => undefined);
  }, [doc, pageIndex, isVisible]);

  return (
    <div css={{ position: 'relative', width, height }}>
      <canvas ref={canvasRef} css={pageCanvasStyle} role='img' aria-label={ariaLabel} />
      {renderError ? (
        <div css={pageErrorStyle} role='alert'>
          <Text as='p' size='small'>
            {t('Unable to render this page. Please try again.')}
          </Text>
          <Button
            variant='secondary'
            size='small'
            label={t('Retry')}
            trackingId={`pdf-page-${pageIndex + 1}-render-retry`}
            onClick={() => {
              setRenderError(false);
              setIsRendered(false);
              setRetryCount((count) => count + 1);
            }}
          />
        </div>
      ) : (
        !isRendered && (
          <div css={pageLoaderStyle}>
            <SpinningLoader size='small' />
          </div>
        )
      )}
    </div>
  );
};
